import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiLoader,
  FiArrowLeft,
  FiCheck,
  FiInfo,
} from "react-icons/fi";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";

const QuestionsManagement = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    quizId: quizId,
    questionText: "",
    options: { A: "", B: "", C: "", D: "" },
    correctOption: "A",
  });

  const fetchQuestions = async () => {
    if (!quizId) return;
    setLoading(true);
    try {
      const res = await makeRequestAuth("post", "/admin/question/list", {
        quizId,
      });
      console.log("Fetch Response:", res);
      if (res?.code === 0) {
        setQuestions(res.data || []);
      } else {
        showErrorToast(res?.message || "Failed to load questions");
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      showErrorToast("Server error while fetching questions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [quizId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let res;
      if (editingId) {
        res = await makeRequestAuth("post", "/admin/question/update", {
          ...formData,
          id: editingId,
        });
      } else {
        res = await makeRequestAuth("post", "/admin/question/add", formData);
      }

      if (res?.code === 0) {
        showSuccessToast(
          editingId
            ? "Question updated successfully"
            : "Question added successfully",
        );
        closeModal();
        fetchQuestions();
      } else {
        showErrorToast(res?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Submit Error:", error);
      showErrorToast("Something went wrong");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this question?"))
      return;
    try {
      const res = await makeRequestAuth("post", "/admin/question/delete", {
        id,
      });
      if (res?.code === 0) {
        showSuccessToast("Question deleted successfully");
        fetchQuestions();
      } else {
        showErrorToast(res?.message || "Delete failed");
      }
    } catch (error) {
      showErrorToast("Unable to delete question");
    }
  };

  const openEditModal = (q) => {
    setEditingId(q._id);
    setFormData({
      quizId: quizId,
      questionNo: q.questionNo,
      questionText: q.questionText,
      options: { ...q.options },
      correctOption: q.correctOption,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({
      quizId: quizId,
      questionNo: questions.length + 1,
      questionText: "",
      options: { A: "", B: "", C: "", D: "" },
      correctOption: "A",
    });
  };

  return (
    <div className="p-4">
      <ToastContainer />

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2.5 bg-white border border-slate-200 rounded-xl shadow-sm hover:bg-slate-50 transition-all text-slate-600"
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
              Question Bank
            </h1>
            <p className="text-sm text-slate-500">
              Managing questions for Quiz ID:{" "}
              <span className="font-mono text-blue-600">{quizId}</span>
            </p>
          </div>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-[#2563eb] text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 shadow-md font-medium active:scale-95 transition-all"
        >
          <FiPlus size={20} /> Add New Question
        </button>
      </div>

      {/* Questions List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <FiLoader className="animate-spin text-blue-600 mb-4" size={40} />
          <p className="text-slate-500">Loading questions...</p>
        </div>
      ) : questions.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-20 flex flex-col items-center text-center">
          <FiInfo size={48} className="text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">
            No Questions Added
          </h3>
          <p className="text-slate-500 max-w-xs mt-1">
            Start building your quiz by adding your first multiple-choice
            question.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {questions.map((q) => (
            <div
              key={q._id}
              className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:border-blue-200 transition-all"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 bg-blue-50 text-blue-600 flex items-center justify-center rounded-full font-bold text-sm border border-blue-100">
                    {q.questionNo}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Question Metadata
                  </span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEditModal(q)}
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  >
                    <FiEdit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(q._id)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </div>

              <p className="text-slate-800 font-bold text-lg mb-6 leading-relaxed">
                {q.questionText}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(q.options).map(([key, value]) => (
                  <div
                    key={key}
                    className={`p-4 rounded-xl border flex items-center justify-between transition-all ${
                      q.correctOption === key
                        ? "bg-green-50 border-green-200 text-green-700 ring-1 ring-green-200"
                        : "bg-slate-50 border-slate-100 text-slate-600"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${q.correctOption === key ? "bg-green-600 text-white" : "bg-slate-200 text-slate-500"}`}
                      >
                        {key}
                      </span>
                      <span className="text-sm font-semibold">{value}</span>
                    </div>
                    {q.correctOption === key && (
                      <FiCheck className="text-green-600" strokeWidth={3} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50/50">
              <h2 className="text-xl font-bold text-slate-800">
                {editingId ? "Update Question" : "Add New Question"}
              </h2>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="p-6 space-y-5 max-h-[80vh] overflow-y-auto custom-scrollbar"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Question Number
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.questionNo}
                    onChange={(e) =>
                      setFormData({ ...formData, questionNo: e.target.value })
                    }
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Correct Answer
                  </label>
                  <select
                    value={formData.correctOption}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        correctOption: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none appearance-none"
                  >
                    <option value="A">Option A</option>
                    <option value="B">Option B</option>
                    <option value="C">Option C</option>
                    <option value="D">Option D</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Question Text
                </label>
                <textarea
                  required
                  rows="3"
                  value={formData.questionText}
                  onChange={(e) =>
                    setFormData({ ...formData, questionText: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none resize-none"
                  placeholder="Enter your question here..."
                ></textarea>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["A", "B", "C", "D"].map((opt) => (
                  <div key={opt}>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2 ml-1">
                      Option {opt}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.options[opt]}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          options: {
                            ...formData.options,
                            [opt]: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                      placeholder={`Choice ${opt}`}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-8 flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-3 border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all active:scale-[0.98]"
                >
                  {editingId ? "Update Question" : "Save Question"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionsManagement;
